import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { ScoringConfig, ScoringRange, ScoringMode } from '@/types/quiz';

interface ScoringConfigEditorProps {
  config: ScoringConfig;
  onChange: (config: ScoringConfig) => void;
}

export function ScoringConfigEditor({ config, onChange }: ScoringConfigEditorProps) {
  const handleModeChange = (mode: ScoringMode) => {
    onChange({ ...config, mode });
  };

  const handleAddRange = () => {
    const ranges = config.score_ranges || [];
    const lastRange = ranges[ranges.length - 1];
    const newMin = lastRange ? (lastRange.max ?? 0) + 1 : 0;
    
    onChange({
      ...config,
      score_ranges: [
        ...ranges,
        { min: newMin, max: newMin + 10, message: '' },
      ],
    });
  };

  const handleRemoveRange = (index: number) => {
    const ranges = [...(config.score_ranges || [])];
    ranges.splice(index, 1);
    onChange({ ...config, score_ranges: ranges });
  };

  const handleRangeChange = (index: number, field: keyof ScoringRange, value: number | string | null) => {
    const ranges = [...(config.score_ranges || [])];
    ranges[index] = { ...ranges[index], [field]: value };
    onChange({ ...config, score_ranges: ranges });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Scoring Mode</Label>
        <Select value={config.mode || 'percentage'} onValueChange={(v) => handleModeChange(v as ScoringMode)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="percentage">Percentage (correct/total × 100)</SelectItem>
            <SelectItem value="count">Count (total checked items)</SelectItem>
            <SelectItem value="points">Points (custom point values)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {config.mode === 'count' 
            ? 'Score is the total number of selected options across all multi-select questions.' 
            : config.mode === 'points'
            ? 'Each question can have custom point values (coming soon).'
            : 'Score is calculated as percentage of correct answers.'}
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Score Ranges</Label>
          <Button variant="outline" size="sm" onClick={handleAddRange}>
            <Plus className="h-4 w-4 mr-2" />
            Add Range
          </Button>
        </div>
        
        <p className="text-xs text-muted-foreground">
          Define result messages for different score ranges. Leave max empty for "and above".
        </p>

        {(config.score_ranges || []).length === 0 ? (
          <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
            No score ranges defined. Add ranges to show custom result messages.
          </div>
        ) : (
          <div className="space-y-3">
            {(config.score_ranges || []).map((range, index) => (
              <div key={index} className="flex items-start gap-3 p-4 border rounded-lg bg-muted/30">
                <div className="flex-1 grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Min Score</Label>
                    <Input
                      type="number"
                      value={range.min}
                      onChange={(e) => handleRangeChange(index, 'min', parseInt(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Max Score</Label>
                    <Input
                      type="number"
                      value={range.max ?? ''}
                      onChange={(e) => handleRangeChange(index, 'max', e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="∞ (no limit)"
                    />
                  </div>
                  <div className="space-y-1 col-span-3">
                    <Label className="text-xs">Result Message</Label>
                    <Input
                      value={range.message}
                      onChange={(e) => handleRangeChange(index, 'message', e.target.value)}
                      placeholder="e.g., You're operating at human-AI synergy level"
                    />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="mt-6 text-destructive hover:text-destructive"
                  onClick={() => handleRemoveRange(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview */}
      {(config.score_ranges || []).length > 0 && (
        <div className="space-y-2 p-4 bg-muted rounded-lg">
          <Label className="text-sm font-medium">Preview</Label>
          <div className="text-sm space-y-1">
            {(config.score_ranges || []).map((range, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="font-mono text-xs bg-background px-2 py-0.5 rounded">
                  {range.min}–{range.max ?? '∞'}
                </span>
                <span className="text-muted-foreground">→</span>
                <span>{range.message || '(no message)'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
